import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConfirmDialog } from './Dialog.js';

// "A form with unsaved changes SHOULD warn before navigation away" (design system 03-components §3.3; Phase 1c finding
// G9). A group's grants and an account's groups are edited as a set and saved once, and leaving the page dropped the
// set without a word — the dialogs confirmed a discard, the two page editors did not. While an editor holds unsaved
// changes this guards the three ways out: a link in the app (the rail, a breadcrumb, a name in a table), the browser's
// Back and Forward, and leaving the app altogether (a reload, closing the tab, another site), which only the browser's
// own prompt may ask about.
//
// Why not the router's blocker: `useBlocker` needs a data router, and Calis runs on `BrowserRouter` — a move that would
// put every route behind the data router's own error boundary in place of the app's (`RegionErrorBoundary`). So a link
// is held at the document before the router hears the click, and Back or Forward is put back before the router hears
// the pop; either goes ahead on "Discard changes".

/** A guard's answer to a Back or Forward: true when it holds the pop, so the router must not hear it. */
export type PopHandler = (event: PopStateEvent) => boolean;

const popHandlers = new Set<PopHandler>();
let popGateInstalled = false;

/**
 * Installs the one window listener through which an armed guard may hold a Back or Forward. Why at boot, from main.tsx,
 * before the app renders: listeners on the window run in the order they were added — a capturing one included, as
 * Chromium dispatches at the window — and the router adds its own as it mounts, so only a listener added before it can
 * stop the pop reaching it. Idempotent; a guard calls it too, for a page rendered without the boot (then late, and
 * unable to hold the pop, as before).
 */
export function installPopGate(target: Window = window): void {
  if (popGateInstalled) return;
  popGateInstalled = true;
  target.addEventListener('popstate', (event) => {
    for (const handler of [...popHandlers].reverse()) {
      if (handler(event)) {
        event.stopImmediatePropagation();
        return;
      }
    }
  });
}

/** Asks `handler` about every Back or Forward until the returned function is called; the latest added is asked first. */
export function holdPops(handler: PopHandler): () => void {
  popHandlers.add(handler);
  return () => { popHandlers.delete(handler); };
}

export interface ClickFacts {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  defaultPrevented: boolean;
}

export interface AnchorFacts {
  href: string;
  target: string;
  download: boolean;
}

/**
 * The address in the app a click on a link would leave this page for, or null where the click leaves nothing — a
 * modified or secondary click (a new tab), a download, a link to another window, the same page (a `#` within it) — or
 * leaves the app for another site, which the browser's own prompt asks about. Pure.
 */
export function leavingDestination(click: ClickFacts, anchor: AnchorFacts, here: { origin: string; pathname: string; search: string }): string | null {
  if (click.defaultPrevented || click.button !== 0 || click.metaKey || click.ctrlKey || click.shiftKey || click.altKey) return null;
  if (anchor.download || (anchor.target !== '' && anchor.target !== '_self')) return null;
  let url: URL;
  try {
    url = new URL(anchor.href, here.origin);
  } catch {
    return null;
  }
  if (url.origin !== here.origin) return null;
  if (url.pathname === here.pathname && url.search === here.search) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

/** The router's index of a history entry, from its state (`{ idx }`); null where it carries none. */
export function historyIndex(state: unknown): number | null {
  const idx = (state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' && Number.isInteger(idx) ? idx : null;
}

/** How many entries a Back or Forward moved, or null where either end's index is unknown or nothing moved. Pure. */
export function popDelta(next: number | null, here: number | null): number | null {
  if (next === null || here === null || next === here) return null;
  return next - here;
}

type Pending = { kind: 'link'; to: string; anchor: HTMLAnchorElement } | { kind: 'pop'; delta: number };

export interface UnsavedChangesGuardProps {
  /** True while the editor holds changes not yet saved (and no save is in flight). */
  when: boolean;
  /** What would be lost, for the question: "the changes to this group's grants". */
  what: string;
}

/** Asks before any way out of the page while `when` holds: a link in the app, Back or Forward, or leaving the app. */
export function UnsavedChangesGuard({ when, what }: UnsavedChangesGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState<Pending | null>(null);
  const [leaving, setLeaving] = useState<Pending | null>(null);
  // Why a ref: once the reader chose to discard, the way out they asked for must pass the guards still listening.
  const allowed = useRef(false);

  useEffect(() => {
    allowed.current = false;
  }, [location.key, when]);

  // Leaving the app: a reload, closing the tab, another site. Only the browser may ask, in its own words.
  useEffect(() => {
    if (!when) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (allowed.current) return;
      event.preventDefault();
      // Why returnValue too: older browsers ask only when it is set.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [when]);

  // A link in the app. Why the document's capture phase: it runs before the router's link hears the click.
  useEffect(() => {
    if (!when) return undefined;
    const onClick = (event: MouseEvent): void => {
      if (allowed.current) return;
      const anchor = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href]') : null;
      if (!anchor) return;
      const to = leavingDestination(event, { href: anchor.href, target: anchor.target, download: anchor.hasAttribute('download') }, { origin: window.location.origin, pathname: location.pathname, search: location.search });
      if (to === null) return;
      event.preventDefault();
      event.stopPropagation();
      setPending({ kind: 'link', to, anchor });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [when, location.pathname, location.search]);

  // Back and Forward, through the pop gate (`installPopGate`), which hears the pop before the router does: the pop is put
  // back (`history.go` the other way) before the router moves the page, and the put-back's own pop is held too. Where the
  // entries carry no index the pop goes ahead unasked, as it did before.
  useEffect(() => {
    if (!when) return undefined;
    installPopGate();
    const here = historyIndex(window.history.state);
    let puttingBack = false;
    const onPop: PopHandler = (event) => {
      if (allowed.current) return false;
      if (puttingBack) {
        puttingBack = false;
        return true;
      }
      const delta = popDelta(historyIndex(event.state), here);
      if (delta === null) return false;
      puttingBack = true;
      window.history.go(-delta);
      setPending({ kind: 'pop', delta });
      return true;
    };
    return holdPops(onPop);
  }, [when]);

  // Why the way out is taken after the question has closed: the open dialog makes the page inert, and a link clicked
  // inside an inert page does nothing. The link is clicked again — so the router's own link, with its replace or state,
  // does what it would have done — or, gone from the page meanwhile, its address is followed.
  useEffect(() => {
    if (!leaving || pending) return;
    setLeaving(null);
    allowed.current = true;
    if (leaving.kind === 'pop') window.history.go(leaving.delta);
    else if (leaving.anchor.isConnected) leaving.anchor.click();
    else navigate(leaving.to);
  }, [leaving, pending, navigate]);

  return (
    <ConfirmDialog
      open={pending !== null}
      title="Discard unsaved changes?"
      confirmLabel="Discard changes"
      cancelLabel="Keep editing"
      danger
      onConfirm={() => {
        setLeaving(pending);
        setPending(null);
      }}
      onCancel={() => setPending(null)}
    >
      <p>{`You have not saved ${what}. Leaving this page now discards them; they cannot be recovered.`}</p>
    </ConfirmDialog>
  );
}
